import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const UploadsStateContext = createContext(null);
const UploadsActionsContext = createContext(null);

export function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatETA(seconds) {
    if (seconds == null || !isFinite(seconds) || seconds < 0) return "--";
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

export function UploadsProvider({ children }) {
    const [uploads, setUploads] = useState([]);
    const abortersRef = useRef({});
    const uploadsRef = useRef(uploads);
    uploadsRef.current = uploads;

    const hasActive = uploads.some((u) => u.status === "uploading");

    useEffect(() => {
        if (!hasActive) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasActive]);

    const addUpload = useCallback((id, { filename, size }) => {
        setUploads((prev) => [...prev, {
            id,
            filename,
            size: size || 0,
            loaded: 0,
            status: "uploading",
            error: null,
            startTime: Date.now(),
            endTime: null,
        }]);
    }, []);

    const updateUpload = useCallback((id, patch) => {
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    }, []);

    const removeUpload = useCallback((id) => {
        setUploads((prev) => prev.filter((u) => u.id !== id));
        delete abortersRef.current[id];
    }, []);

    const setAborter = useCallback((id, fn) => {
        abortersRef.current[id] = fn;
    }, []);

    const cancelUpload = useCallback((id) => {
        abortersRef.current[id]?.();
        setUploads((prev) => prev.map((u) =>
            u.id === id && u.status === "uploading"
                ? { ...u, status: "cancelled", error: "Cancelled", endTime: Date.now() }
                : u
        ));
    }, []);

    const cancelAll = useCallback(() => {
        uploadsRef.current.forEach((u) => {
            if (u.status === "uploading") abortersRef.current[u.id]?.();
        });
        setUploads((prev) => prev.map((u) =>
            u.status === "uploading"
                ? { ...u, status: "cancelled", error: "Cancelled", endTime: Date.now() }
                : u
        ));
    }, []);

    const clearCompleted = useCallback(() => {
        setUploads((prev) => prev.filter((u) => u.status === "uploading"));
    }, []);

    const actions = useMemo(() => ({
        addUpload,
        updateUpload,
        removeUpload,
        setAborter,
        cancelUpload,
        cancelAll,
        clearCompleted,
    }), [addUpload, updateUpload, removeUpload, setAborter, cancelUpload, cancelAll, clearCompleted]);

    return (
        <UploadsActionsContext.Provider value={actions}>
            <UploadsStateContext.Provider value={uploads}>
                {children}
            </UploadsStateContext.Provider>
        </UploadsActionsContext.Provider>
    );
}

export function useUploads() {
    return useContext(UploadsStateContext);
}

export function useUploadActions() {
    return useContext(UploadsActionsContext);
}
